import {
	ArrowDown01Icon,
	Copy01Icon,
	Home01Icon,
	RefreshIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { env } from "@/env";
import { Button } from "./button";
import type { ErrorFallbackProps } from "./error-boundary";
import { GradientBackground } from "./gradient-background";
import { SlapLogo } from "./slap-logo";

const isDev = env.MODE === "development";
function ErrorFallback({
	error,
	resetErrorBoundary,
	componentStack,
}: ErrorFallbackProps) {
	const [showDetails, setShowDetails] = useState(false);
	const [copied, setCopied] = useState(false);

	const safeName = error?.name ?? "UnknownError";

	const errorData = isBackendError(error) ? error.data : null;

	const getCustomMessage = () => {
		if (!errorData) return error?.message ?? String(error);

		switch (errorData.code) {
			case "CONCURRENT_LIMIT_EXCEEDED":
				return `Server is currently busy handling many requests (Limit: ${errorData.limit}). Please try again in a few seconds.`;
			case "RATE_LIMIT_EXCEEDED": {
				const retrySeconds = Math.ceil((errorData.retryAfter ?? 5000) / 1000);
				return `Too many requests. Please wait ${retrySeconds}s before trying again.`;
			}
			default:
				return error?.message ?? String(error);
		}
	};

	const safeMessage = getCustomMessage();

	const safeStack = error?.stack ?? "No stack trace available";
	const errorDetails = `${safeName}: ${safeMessage}\n\nStack Trace:\n${safeStack}\n\nComponent Stack:\n${componentStack ?? "No component stack available"}`;

	const copyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(errorDetails);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			console.error("Failed to copy to clipboard");
		}
	};

	const handleGoHome = () => {
		window.location.href = "/";
	};

	return (
		<>
			<motion.div
				className="relative z-10 flex h-screen w-screen flex-col items-center justify-center gap-6 p-4"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.3 }}
			>
				{/* Logo in top-left as home link */}
				<a
					href="/"
					className="absolute left-4 top-4 text-foreground transition-opacity hover:opacity-70"
				>
					<SlapLogo width={100} animate={false} />
				</a>

				{/* Large centered error text with gradient */}
				<motion.h1
					className="bg-gradient-to-r from-black to-neutral-600 bg-clip-text text-5xl font-semibold text-transparent"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1, duration: 0.4 }}
				>
					Oops!
				</motion.h1>

				<motion.p
					className="max-w-md text-center text-muted-foreground"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.15, duration: 0.4 }}
				>
					Something went wrong. Please try again or return to the home page.
				</motion.p>

				{/* Action buttons */}
				<motion.div
					className="flex gap-3"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2, duration: 0.4 }}
				>
					{resetErrorBoundary && (
						<Button onClick={resetErrorBoundary}>
							<HugeiconsIcon icon={RefreshIcon} strokeWidth={2} />
							Try Again
						</Button>
					)}
					<Button onClick={handleGoHome} variant="outline">
						<HugeiconsIcon icon={Home01Icon} strokeWidth={2} />
						Go Home
					</Button>
				</motion.div>

				{/* Dev-only expandable details */}
				{isDev && (
					<motion.div
						className="mt-4 w-full max-w-lg"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.4 }}
					>
						<button
							type="button"
							onClick={() => setShowDetails(!showDetails)}
							className="flex w-full items-center justify-center gap-1.5 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							<motion.span
								animate={{ rotate: showDetails ? 180 : 0 }}
								transition={{ duration: 0.2 }}
							>
								<HugeiconsIcon
									icon={ArrowDown01Icon}
									strokeWidth={2}
									className="size-4"
								/>
							</motion.span>
							{showDetails ? "Hide" : "Show"} error details
						</button>

						<AnimatePresence>
							{showDetails && (
								<motion.div
									initial={{ height: 0, opacity: 0 }}
									animate={{ height: "auto", opacity: 1 }}
									exit={{ height: 0, opacity: 0 }}
									transition={{ duration: 0.2 }}
									className="overflow-hidden"
								>
									<div className="mt-2 rounded-lg border bg-background/80 p-4 backdrop-blur-sm">
										<div className="mb-2 flex items-center justify-between">
											<span className="text-xs font-medium text-destructive">
												{safeName}
											</span>
											<Button
												variant="ghost"
												size="icon-sm"
												onClick={copyToClipboard}
												title="Copy error details"
											>
												<HugeiconsIcon
													icon={Copy01Icon}
													strokeWidth={2}
													className="size-3.5"
												/>
											</Button>
										</div>
										<p className="mb-3 text-sm text-foreground">
											{safeMessage}
										</p>
										<pre className="max-h-40 overflow-auto rounded bg-muted/50 p-2 text-xs text-muted-foreground">
											<code>{safeStack}</code>
										</pre>
										{componentStack && (
											<>
												<p className="mb-1 mt-3 text-xs font-medium text-muted-foreground">
													Component Stack:
												</p>
												<pre className="max-h-32 overflow-auto rounded bg-muted/50 p-2 text-xs text-muted-foreground">
													<code>{componentStack}</code>
												</pre>
											</>
										)}
										{copied && (
											<p className="mt-2 text-center text-xs text-primary">
												Copied to clipboard!
											</p>
										)}
									</div>
								</motion.div>
							)}
						</AnimatePresence>

						<p className="mt-2 text-center text-xs text-muted-foreground">
							Development mode - error details visible
						</p>
					</motion.div>
				)}
			</motion.div>

			<GradientBackground />
		</>
	);
}

export { ErrorFallback };

interface BackendError {
	data: {
		code?: string;
		limit?: number;
		retryAfter?: number;
	};
}

/**
 * Type guard to check if an error object contains backend-specific data.
 * Used to provide custom messages for limit-related errors from Convex.
 */
function isBackendError(error: unknown): error is BackendError {
	return (
		error !== null &&
		typeof error === "object" &&
		"data" in error &&
		typeof (error as Record<string, unknown>).data === "object"
	);
}
